/**
 * User Management Service
 * Handles CRUD operations for users in the admin dashboard
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  UserProfile,
  UserCreateData,
  UserUpdateData,
  UserListFilters,
  UserStats,
  UserStatus,
} from '../types/user';
import { UserRole, getPermissions } from '../types/permissions';
import { logAuditEvent } from './auditLog';

const USERS_COLLECTION = 'users';

/**
 * Get a single user by UID
 */
export const getUser = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    
    if (!userDoc.exists()) {
      return null;
    }

    return {
      uid: userDoc.id,
      ...userDoc.data(),
    } as UserProfile;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw new Error('Failed to fetch user');
  }
};

/**
 * Get list of users with optional filters
 */
export const getUsers = async (
  filters: UserListFilters = {}
): Promise<{ users: UserProfile[]; total: number }> => {
  try {
    const constraints: QueryConstraint[] = [];

    // Apply filters
    if (filters.role) {
      constraints.push(where('role', '==', filters.role));
    }

    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    // Apply sorting
    const sortField = filters.sortBy || 'metadata.createdAt';
    const sortDirection = filters.sortOrder || 'desc';
    constraints.push(orderBy(sortField, sortDirection));

    // Apply pagination
    if (filters.limit) {
      constraints.push(firestoreLimit(filters.limit));
    }

    const usersQuery = query(collection(db, USERS_COLLECTION), ...constraints);
    const snapshot = await getDocs(usersQuery);

    let users = snapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data(),
    })) as UserProfile[];

    // Apply search filter (client-side for now)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      users = users.filter(
        user =>
          user.name.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    return {
      users,
      total: users.length,
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error('Failed to fetch users');
  }
};

/**
 * Create a new user
 */
export const createUser = async (
  data: UserCreateData,
  createdBy: string
): Promise<UserProfile> => {
  try {
    const now = Timestamp.now();
    const permissions = getPermissions(data.role);

    const userData: Omit<UserProfile, 'uid'> = {
      email: data.email,
      name: data.name,
      role: data.role,
      phone: data.phone,
      address: data.address,
      status: 'active' as UserStatus,
      permissions: permissions,
      metadata: {
        createdAt: now,
        createdBy,
        lastModifiedAt: now,
        lastModifiedBy: createdBy,
        loginCount: 0,
      },
      settings: {
        language: 'en',
        timezone: 'America/Toronto',
        notifications: {
          email: true,
          push: true,
          sms: false,
          marketing: false,
        },
      },
    };

    const docRef = await addDoc(collection(db, USERS_COLLECTION), userData);

    await logAuditEvent({
      action: 'user.create',
      resource: 'users',
      resourceId: docRef.id,
      details: {
        email: data.email,
        role: data.role,
      },
      performedBy: createdBy,
    });

    return {
      uid: docRef.id,
      ...userData,
    };
  } catch (error) {
    console.error('Error creating user:', error);
    throw new Error('Failed to create user');
  }
};

/**
 * Update an existing user
 */
export const updateUser = async (
  uid: string,
  data: UserUpdateData,
  updatedBy: string
): Promise<void> => {
  try {
    const updateData: any = {
      ...data,
      'metadata.lastModifiedAt': Timestamp.now(),
      'metadata.lastModifiedBy': updatedBy,
    };

    await updateDoc(doc(db, USERS_COLLECTION, uid), updateData);

    await logAuditEvent({
      action: 'user.update',
      resource: 'users',
      resourceId: uid,
      details: data,
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating user:', error);
    throw new Error('Failed to update user');
  }
};

/**
 * Update user role (superadmin only)
 */
export const updateUserRole = async (
  uid: string,
  newRole: UserRole,
  updatedBy: string
): Promise<void> => {
  try {
    const permissions = getPermissions(newRole);

    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      role: newRole,
      permissions,
      'metadata.lastModifiedAt': Timestamp.now(),
      'metadata.lastModifiedBy': updatedBy,
    });

    await logAuditEvent({
      action: 'user.role_change',
      resource: 'users',
      resourceId: uid,
      details: {
        newRole,
      },
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error('Failed to update user role');
  }
};

/**
 * Suspend or activate a user
 */
export const updateUserStatus = async (
  uid: string,
  status: UserStatus,
  updatedBy: string,
  reason?: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      status,
      'metadata.lastModifiedAt': Timestamp.now(),
      'metadata.lastModifiedBy': updatedBy,
    });

    await logAuditEvent({
      action: status === 'suspended' ? 'user.suspend' : 'user.activate',
      resource: 'users',
      resourceId: uid,
      details: {
        status,
        reason,
      },
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating user status:', error);
    throw new Error('Failed to update user status');
  }
};

/**
 * Delete a user (soft delete - set status to inactive)
 */
export const deleteUser = async (
  uid: string,
  deletedBy: string,
  reason?: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      status: 'inactive' as UserStatus,
      'metadata.lastModifiedAt': Timestamp.now(),
      'metadata.lastModifiedBy': deletedBy,
    });

    await logAuditEvent({
      action: 'user.delete',
      resource: 'users',
      resourceId: uid,
      details: {
        reason,
      },
      performedBy: deletedBy,
    });
  } catch (error) {
    console.error('Error deleting user:', error);
    throw new Error('Failed to delete user');
  }
};

/**
 * Hard delete a user (permanently remove from database)
 * Use with extreme caution - superadmin only
 */
export const permanentlyDeleteUser = async (
  uid: string,
  deletedBy: string
): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    const userData = userDoc.data();

    await deleteDoc(doc(db, USERS_COLLECTION, uid));

    await logAuditEvent({
      action: 'user.permanent_delete',
      resource: 'users',
      resourceId: uid,
      details: {
        email: userData?.email,
        name: userData?.name,
      },
      performedBy: deletedBy,
    });
  } catch (error) {
    console.error('Error permanently deleting user:', error);
    throw new Error('Failed to permanently delete user');
  }
};

/**
 * Get user statistics
 */
export const getUserStats = async (): Promise<UserStats> => {
  try {
    console.log('[getUserStats] Starting stats fetch...');
    const snapshot = await getDocs(collection(db, USERS_COLLECTION));
    console.log('[getUserStats] Got snapshot with', snapshot.docs.length, 'documents');
    
    const users = snapshot.docs.map(doc => doc.data() as UserProfile);
    console.log('[getUserStats] Mapped', users.length, 'users');

    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats: UserStats = {
      totalUsers: users.length,
      activeUsers: users.filter(u => u.status === 'active').length,
      suspendedUsers: users.filter(u => u.status === 'suspended').length,
      newThisMonth: users.filter(
        u => u.metadata?.createdAt?.toDate && u.metadata.createdAt.toDate() >= firstDayOfMonth
      ).length,
      byRole: {
        superadmin: users.filter(u => u.role === 'superadmin').length,
        admin: users.filter(u => u.role === 'admin').length,
        sales: users.filter(u => u.role === 'sales').length,
        support: users.filter(u => u.role === 'support').length,
        accountant: users.filter(u => u.role === 'accountant').length,
        landlord: users.filter(u => u.role === 'landlord').length,
      },
    };

    console.log('[getUserStats] Calculated stats:', stats);
    return stats;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    console.error('[getUserStats] Error details:', {
      code: error.code,
      message: error.message,
      firebaseError: error.name
    });
    throw new Error('Failed to fetch user stats');
  }
};

/**
 * Update user's last login timestamp
 */
export const updateLastLogin = async (uid: string): Promise<void> => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      'metadata.lastLogin': Timestamp.now(),
      'metadata.loginCount': (await getUser(uid))!.metadata.loginCount + 1,
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    // Don't throw - this is not critical
  }
};
